import type { ReactNode } from 'react';
import { useConnection, useLive, useNow, useRuntime } from './context';
import { BrandMark } from './brand';
import { useStaffBranding } from './useRemote';
import { formatClock, formatStaleness } from '../mirror/time';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';

/**
 * The strip along the top: **is this book current, and does the cloud know
 * everything this iPad has been told?**
 *
 * It exists because spec §8.1 asks for it in as many words — *"the UI says
 * plainly how stale it is"* — and because the alternative is the failure mode
 * this whole design is a reaction to: a device that looks the same whether it is
 * showing the truth or a memory of the truth.
 *
 * Three facts, always visible, never a spinner:
 *
 * - **the connection**, in the plainest word available;
 * - **the age of the mirror**, as a clock time, so the owner can decide for
 *   themselves whether four minutes matters for what they are about to do;
 * - **the outbox depth**, because "3 waiting to send" is the difference between
 *   an iPad that is behind and an iPad that has lost something.
 */
export function StatusStrip({ onSignOut }: { onSignOut: () => void }): ReactNode {
  const runtime = useRuntime();
  const connection = useConnection();
  const now = useNow();
  const t = useT();
  const locale = useLocale();

  const state = useLive(() => runtime.mirror.state(), {
    session: null,
    lastSyncAt: null,
    cursor: null,
    window: null,
  });
  const depth = useLive(() => runtime.outbox.depth(), 0);
  const tasks = useLive(() => runtime.outbox.rejected(), []);

  const timezone = state.session?.tenant.timezone ?? 'UTC';
  const tenantName = state.session?.tenant.name ?? '—';
  const status = state.session?.tenant.status;
  const branding = useStaffBranding();

  /**
   * **The app's title area is the strip**, so this is where the wordmark goes.
   *
   * LoveSpa floats its `.brandmark` absolutely over the page, above content that
   * may sit slightly under it — its own comment calls that by design on a screen
   * with one column and no chrome. This app has chrome: a sticky strip that
   * already carries the business's name and never moves. Putting the wordmark
   * anywhere else would have been a second identity on the same screen, so
   * `brandmark: "hearts"` *replaces* the plain name rather than joining it, and
   * the strip keeps its shape either way.
   *
   * `plain` (and an absent document) renders exactly the `<span>` that was here
   * before, with the same class on it.
   */
  const brandmark = branding?.brandmark === 'hearts' ? 'hearts' : 'plain';

  /**
   * **The support banner** — *whose eyes these are, and when they close.*
   *
   * Persistent, never dismissable, and on the strip rather than as a toast:
   * §8.1's rule is that the UI says plainly what it is showing, and an
   * operator looking through an owner's book is the one state where "what
   * this screen is" matters more than how stale it is. The credential is the
   * first source (`runtime.supportSession()`, so the banner paints on the very
   * first frame, before any hydrate) and the mirrored session is the second
   * (`session.support`, what the server says it accepted); they name the same
   * operator and the same minute, and the first is preferred only because it
   * exists earlier.
   */
  const supportFromToken = runtime.supportSession();
  const support =
    supportFromToken !== null
      ? { operator: supportFromToken.operator, expiresAt: supportFromToken.expiresAt }
      : state.session?.support === undefined
        ? null
        : { operator: state.session.support.operator, expiresAt: state.session.support.expires_at };

  return (
    <header className={`status-strip${support === null ? '' : ' status-strip--support'}`}>
      {support !== null ? (
        <div className="status-strip__support" role="status">
          {t('status.supportBanner', {
            operator: support.operator,
            until: formatClock(support.expiresAt, timezone, locale),
          })}
        </div>
      ) : null}
      <div className="status-strip__identity">
        <span className="status-strip__tenant">
          {brandmark === 'hearts' ? <BrandMark name={tenantName} style="hearts" /> : tenantName}
        </span>
        {/* `status` is the server's word for the tenant's lifecycle and is
            printed exactly as `GET /v1/session` sent it. */}
        {status !== undefined && status !== 'live' ? (
          <span className="badge badge--warn" title={t('status.notLiveTitle')}>
            {status}
          </span>
        ) : null}
      </div>

      <div className="status-strip__facts">
        <span className={`dot dot--${connection}`} aria-hidden="true" />
        <span className="status-strip__connection">{connectionWord(t, connection)}</span>
        <span className="status-strip__staleness">
          {formatStaleness(state.lastSyncAt, now, timezone, locale)}
        </span>
        {depth > 0 ? (
          <span className="badge badge--pending">
            {t('status.waitingToSend', { count: depth })}
          </span>
        ) : null}
        {tasks.length > 0 ? (
          <span className="badge badge--task">{t('status.needsYou', { count: tasks.length })}</span>
        ) : null}
      </div>

      <button type="button" className="link-button" onClick={onSignOut}>
        {support === null ? t('status.signOut') : t('status.supportEnd')}
      </button>
    </header>
  );
}

function connectionWord(t: T, state: string): string {
  if (state === 'live') return t('status.online');
  if (state === 'connecting') return t('status.reconnecting');
  return t('status.offline');
}
